import { useCallback, useLayoutEffect, useRef } from 'react';

const DEFAULT_SELECTOR = '[data-module-id]';
const DEFAULT_DURATION_MS = 220;
const DEFAULT_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const DEFAULT_MEASURE = 'rect';
const MIN_DELTA = 1;

function buildTransform(deltaX, deltaY, baseTransform) {
  const translate = `translate3d(${deltaX}px, ${deltaY}px, 0)`;
  if (!baseTransform || baseTransform === 'none') {
    return translate;
  }
  return `${translate} ${baseTransform}`;
}

function getLayoutOffset(element) {
  let left = 0;
  let top = 0;
  let current = element;

  while (current) {
    left += current.offsetLeft || 0;
    top += current.offsetTop || 0;
    current = current.offsetParent;
  }

  return { left, top };
}

function getElementPosition(element, container, measure) {
  if (measure === 'offset') {
    const elementOffset = getLayoutOffset(element);
    const containerOffset = getLayoutOffset(container);
    return {
      left: elementOffset.left - containerOffset.left,
      top: elementOffset.top - containerOffset.top,
    };
  }

  const rect = element.getBoundingClientRect();
  return {
    left: rect.left,
    top: rect.top,
  };
}

export function useListFlip(containerRef, options = {}) {
  const {
    selector = DEFAULT_SELECTOR,
    dataKey = 'moduleId',
    durationMs = DEFAULT_DURATION_MS,
    easing = DEFAULT_EASING,
    measure = DEFAULT_MEASURE,
  } = options;

  const capturedPositionsRef = useRef(new Map());
  const rafRef = useRef(null);
  const cleanupTimersRef = useRef([]);
  const activeAnimationsRef = useRef([]);

  const cleanupAnimation = useCallback((animation) => {
    if (!animation) return;

    const {
      element,
      previousTransition,
      previousTransform,
      previousWillChange,
      onEnd,
      timer,
    } = animation;

    if (timer) {
      clearTimeout(timer);
    }
    if (onEnd) {
      element.removeEventListener('transitionend', onEnd);
    }

    element.style.transition = previousTransition;
    element.style.transform = previousTransform;
    element.style.willChange = previousWillChange;
  }, []);

  const cancelAnimations = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    cleanupTimersRef.current.forEach(clearTimeout);
    cleanupTimersRef.current = [];

    activeAnimationsRef.current.forEach(cleanupAnimation);
    activeAnimationsRef.current = [];
  }, [cleanupAnimation]);

  const capturePositions = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    cancelAnimations();

    const positions = new Map();
    const elements = container.querySelectorAll(selector);

    elements.forEach((element) => {
      const id = element.dataset?.[dataKey];
      if (!id) return;

      positions.set(id, getElementPosition(element, container, measure));
    });

    capturedPositionsRef.current = positions;
  }, [cancelAnimations, containerRef, dataKey, measure, selector]);

  const animateFromCapture = useCallback((animationOptions = {}) => {
    const container = containerRef.current;
    const capturedPositions = capturedPositionsRef.current;
    const excludeIds = animationOptions.excludeIds || new Set();
    if (!container || capturedPositions.size === 0) {
      return;
    }

    capturedPositionsRef.current = new Map();
    cancelAnimations();

    const animations = [];
    const elements = container.querySelectorAll(selector);

    elements.forEach((element) => {
      const id = element.dataset?.[dataKey];
      if (!id) return;
      if (excludeIds.has(id)) return;

      const previousRect = capturedPositions.get(id);
      if (!previousRect) return;

      const nextRect = getElementPosition(element, container, measure);
      const deltaX = previousRect.left - nextRect.left;
      const deltaY = previousRect.top - nextRect.top;

      if (Math.abs(deltaX) < MIN_DELTA && Math.abs(deltaY) < MIN_DELTA) {
        return;
      }

      const previousTransition = element.style.transition;
      const previousTransform = element.style.transform;
      const previousWillChange = element.style.willChange;

      element.style.transition = 'none';
      element.style.willChange = 'transform';
      element.style.transform = buildTransform(deltaX, deltaY, previousTransform);

      animations.push({
        element,
        previousTransition,
        previousTransform,
        previousWillChange,
        onEnd: null,
        timer: null,
      });
    });

    if (animations.length === 0) {
      return;
    }

    activeAnimationsRef.current = animations;

    animations.forEach((animation) => {
      const timer = setTimeout(() => {
        cleanupAnimation(animation);
        activeAnimationsRef.current = activeAnimationsRef.current.filter(
          (entry) => entry !== animation
        );
      }, durationMs + 150);

      animation.timer = timer;
      cleanupTimersRef.current.push(timer);
    });

    void container.offsetHeight;

    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;

      animations.forEach((animation) => {
        const { element, previousTransform } = animation;

        element.style.transition = `transform ${durationMs}ms ${easing}`;
        element.style.transform = previousTransform || '';

        const onEnd = (event) => {
          if (event.propertyName !== 'transform') {
            return;
          }

          cleanupAnimation(animation);
          activeAnimationsRef.current = activeAnimationsRef.current.filter(
            (entry) => entry !== animation
          );
        };

        animation.onEnd = onEnd;
        element.addEventListener('transitionend', onEnd);

      });
    });
  }, [
    cancelAnimations,
    cleanupAnimation,
    containerRef,
    dataKey,
    durationMs,
    easing,
    measure,
    selector,
  ]);

  useLayoutEffect(() => {
    return () => {
      cancelAnimations();
      capturedPositionsRef.current = new Map();
    };
  }, [cancelAnimations]);

  return {
    animateFromCapture,
    cancelAnimations,
    capturePositions,
    capturedPositionsRef,
  };
}
